import React, { PropsWithChildren, useEffect } from 'react';
import { RouteType } from '@/models/RouteType';
import { useLocation } from 'react-router-dom';
import { MainNavigation } from '@/components/MainNavigation/MainNavigation';
import { ContainerPageFluid, ContainerPageMiddle } from '@/components/Page/Page';
import {
    fetchClientConfiguration,
    fetchCountriesList,
    fetchMccList,
} from '../reduxStore/slices/comonTermsSlice';
import { RootState, useAppDispatch, useAppSelector } from '../reduxStore/rootReducer';
import { GlobalModals } from '../components/GlobalModals/GlobalModals';
import { useAuth0 } from '@auth0/auth0-react';
import styled from 'styled-components';
import { DesktopRecommendedBanner } from '@monavate/ui-core';
import { HEADER_HEIGHT } from '@/styles/layout';
import { useIsUpToTablet } from '@/hooks/useMediaQuery';

const LayoutWrapper = styled.div`
    padding-top: ${HEADER_HEIGHT}px;
    position: relative;
`;
const ContentArea = styled.div<{ $flush?: boolean }>`
    /* Below desktop, remittance opens with the expiry pill, which the design puts directly
       beneath the header — the standard gap would detach it. */
    margin-top: ${(props) => (props.$flush ? 0 : '31px')};
`;
interface DashboardLayoutPropsType {
    routes: Array<RouteType>;
}

export const DashboardLayout: React.FC<DashboardLayoutPropsType> = ({
    children,
}: PropsWithChildren<DashboardLayoutPropsType>) => {
    const dispatch = useAppDispatch();
    const { isAuthenticated } = useAuth0();
    const clientConfiguration = useAppSelector((state: RootState) => state.commonTerms.config);
    const { pathname } = useLocation();

    // Remittance is the one public, shareable flow with a layout built for phones and
    // tablets, so below desktop it opts out of both the desktop-width container and the
    // "desktop recommended" banner. On desktop it kept its original layout, so it takes
    // the same treatment as every other page.
    const isUpToTablet = useIsUpToTablet();
    const isResponsivePage = pathname.includes('remittance') && isUpToTablet;
    const Container = isResponsivePage ? ContainerPageFluid : ContainerPageMiddle;

    useEffect(() => {
        if (!isAuthenticated) return;

        dispatch(fetchCountriesList());
        dispatch(fetchMccList());
        dispatch(fetchClientConfiguration());
    }, [isAuthenticated]);

    return (
        <LayoutWrapper>
            <MainNavigation />
            {!isResponsivePage && <DesktopRecommendedBanner />}
            <ContentArea $flush={isResponsivePage}>
                <Container>{children}</Container>
            </ContentArea>
            <GlobalModals />
        </LayoutWrapper>
    );
};
